import type { ReactNode, Key } from 'react'

import type { SelectOptionType } from '../form/components/fields/select-field'
import type { KeyOfDepth } from '../table/types/utils'

// Pomocná funkce pro převod unie na intersection
type UnionToIntersection<T> = (T extends any ? (x: T) => void : never) extends (x: infer R) => void
    ? R
    : never
type IsUnion<T> = [T] extends [UnionToIntersection<T>] ? false : true
type FilterByKey<T, K extends string> =
    IsUnion<T> extends true
    ? T extends T // Unwrap the union type to individually check the object types
    ? K extends keyof T
    ? { [P in keyof T]: P extends K ? NonNullable<T[P]> : T[P] }
    : never
    : never
    : T

type TypeAtPath<T, P extends string> = P extends `${infer K}.${infer Rest}` // is `P` composed of multiple parts
    ? K extends `${number}` // is first part number?
    ? NonNullable<T> extends Array<infer U> // if T is array
    ? TypeAtPath<U, Rest> // extract array element
    : never // T is not array, invalid path
    : K extends keyof NonNullable<FilterByKey<T, K>> // first part is not number, is K in T?
    ? TypeAtPath<NonNullable<NonNullable<FilterByKey<T, K>>[K]>, Rest> // take K and call with <T[K]>
    : never // K is not in T, invalid path
    : P extends `${number}` // P is not composed
    ? NonNullable<T> extends Array<infer U> // P is number
    ? U // T is array, return element type
    : never // T is not array, invalid path
    : P extends keyof FilterByKey<T, P> // is P in T?
    ? FilterByKey<T, P>[P] // return T[P]
    : never // P is not in T, invalid path

/**
 * Returns value from the object based on the path specified as a string.
 *
 * @param obj - The object from which the value is retrieved.
 * @param path - A string representing the path to the requested value in the object (e.g. 'person.address.city').
 * @returns The value on the specified path in the object. If the path is not valid, undefined or defaultValue is returned.
 */

export function GetValueByPath<T, Path extends KeyOfDepth<T>, Default = undefined>(
    obj: T,
    path: Path,
    defaultValue?: Default
): TypeAtPath<T, Path> | Default | undefined {
    const keys = path.split('.')
    let result: any = obj

    for (const key of keys) {
        if (result && typeof result === 'object')
            result = result[key]
        else
            return defaultValue
    }

    if (result === undefined && defaultValue !== undefined)
        return defaultValue

    return result
}

/**
 * Sets a value on the object at a dot-separated path, creating intermediate objects as needed.
 * Mutates `obj` and returns it.
 */
export const SetValueByPath = <T extends Record<string, any>>(obj: T, path: string, value: unknown): T => {
    const keys = path.split('.')
    let current: any = obj

    keys.slice(0, -1).forEach(key => {
        if (current[key] === null || typeof current[key] !== 'object')
            current[key] = {}

        current = current[key]
    })

    current[keys[keys.length - 1]] = value
    return obj
}

/**
 * Deletes the value at a dot-separated path and removes parent objects that became empty.
 * Mutates `obj` and returns it.
 */
export const UnsetValueByPath = <T extends Record<string, any>>(obj: T, path: string): T => {
    const keys = path.split('.')
    const parents: any[] = [obj]

    for (const key of keys.slice(0, -1)) {
        const next = parents[parents.length - 1]?.[key]
        if (next === null || typeof next !== 'object')
            return obj

        parents.push(next)
    }

    delete parents[parents.length - 1][keys[keys.length - 1]]

    for (let i = parents.length - 1; i > 0; i--) {
        if (Array.isArray(parents[i]) || Object.keys(parents[i]).length > 0)
            break

        delete parents[i - 1][keys[i - 1]]
    }

    return obj
}

export const CapitalizeFirstLetter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export const InterleaveArray = (
    elems: ReactNode[],
    separator: ((key: Key) => ReactNode) | ReactNode
): ReactNode[] =>
    ([] as ReactNode[])
        .concat(
            ...elems.map((element, idx) => [
                element,
                typeof separator === 'function' ? separator(`separator_${idx}`) : separator,
            ])
        )
        .slice(0, -1)

export const HasIntersection = <T>(arr1: T[], arr2: T[]) => {
    const set2 = new Set(arr2)
    return arr1.some(item => set2.has(item))
}

export const ConvertToSelectOptions = <T extends readonly (string | number)[]>(
    arr: T,
    toLowerCase: boolean = false,
    removeWhiteSpaces: boolean = false,
): SelectOptionType[] =>
    arr.map(item => {
        let value = toLowerCase && typeof item === 'string' ? item.toLocaleLowerCase() : item
        if (removeWhiteSpaces)
            value = value.toString().replaceAll(' ', '')

        return {
            label: item,
            value
        }
    })

export const HasFlag = (flags: number, flag: number) => (flags & flag) !== 0

export const IsNullish = (value: unknown) => value === undefined || value === null

export const IsStringEmpty = (value: unknown) => IsNullish(value) || value === ''

/** Awaits `promise` and returns `[data]` or `[undefined, error]`; non-Error rejections are wrapped in an Error. */
export const tryCatch = async <T>(promise: Promise<T>): Promise<[T] | [undefined, Error]> => {
    try {
        const data = await promise
        return [data]
    }
    catch (e) {
        return [undefined, e instanceof Error ? e : new Error(String(e))]
    }
}
